// L9: the status API now owns display pricing as well as which package a user
// may buy (`offer.pricing`, sourced from the same resolved plan the checkout
// initializes). These terms remain as the FALLBACK only, so this client keeps
// rendering correctly against a server that predates that field — and so a
// catalog price change no longer has to be applied here by hand.
export const SUBSCRIPTION_DISPLAY_TERMS = Object.freeze({
  name: 'DİKO Uzman Paketi',
  netPrice: 2250,
  vatRate: 0.20,
  grossPrice: 2700,
  currency: 'TRY',
})

// Provider/catalog names remain untouched so administrative surfaces can
// distinguish the immutable plans. Customer-facing subscription screens use
// this keyed label instead of exposing campaign-specific catalog wording.
export const CUSTOMER_PLAN_DISPLAY_NAMES = Object.freeze({
  default: 'DİKO Aylık Abonelik',
  launch_promo: 'Diko Aylık Abonelik Paketi',
})

const finiteNonNegative = value => {
  const number = Number(value)
  return Number.isFinite(number) && number >= 0 ? number : null
}

// A partial or malformed pricing payload is discarded whole rather than
// merged field-by-field: quoting a server net price against a fallback gross
// price would show the customer an inconsistent breakdown.
const normalizePricing = value => {
  if (!value || typeof value !== 'object') return null

  const netPrice = finiteNonNegative(value.netPrice)
  const grossPrice = finiteNonNegative(value.grossPrice)
  const vatRate = finiteNonNegative(value.vatRate)
  const currency = typeof value.currency === 'string' && value.currency.trim()
    ? value.currency.trim()
    : null
  if (netPrice == null || grossPrice == null || vatRate == null || !currency) return null

  return Object.freeze({
    name: typeof value.name === 'string' && value.name.trim()
      ? value.name.trim()
      : SUBSCRIPTION_DISPLAY_TERMS.name,
    netPrice,
    vatRate,
    grossPrice,
    currency,
  })
}

export const commercialTerms = offer => {
  const pricing = offer && typeof offer === 'object' ? normalizePricing(offer.pricing) : null
  return pricing || SUBSCRIPTION_DISPLAY_TERMS
}

export const customerCommercialTerms = offer => {
  const terms = commercialTerms(offer)
  const planKey = offer && typeof offer.planKey === 'string' ? offer.planKey.trim() : ''
  const name = CUSTOMER_PLAN_DISPLAY_NAMES[planKey]
  if (!name || name === terms.name) return terms

  return Object.freeze({
    ...terms,
    name,
  })
}

export const normalizeCommercialOffer = value => {
  if (!value || typeof value !== 'object') return null

  const blocked = Boolean(value.blocked)
  const trialPeriodDays = value.trialPeriodDays == null
    ? 0
    : finiteNonNegative(value.trialPeriodDays)
  const recurrenceCount = finiteNonNegative(value.recurrenceCount)
  const planKey = typeof value.planKey === 'string' && value.planKey.trim()
    ? value.planKey.trim()
    : null

  if (!blocked && (!planKey || trialPeriodDays == null || !recurrenceCount)) return null

  return Object.freeze({
    planKey,
    promotional: Boolean(value.promotional),
    trialPeriodDays,
    recurrenceCount: recurrenceCount || 0,
    blocked,
    blockedUntil: value.blockedUntil || null,
    pricing: normalizePricing(value.pricing),
  })
}

export const sameCommercialOffer = (left, right) => {
  const a = normalizeCommercialOffer(left)
  const b = normalizeCommercialOffer(right)
  if (!a || !b) return false
  return [
    'planKey',
    'promotional',
    'trialPeriodDays',
    'recurrenceCount',
    'blocked',
    'blockedUntil',
  ].every(key => String(a[key] == null ? '' : a[key]) === String(b[key] == null ? '' : b[key]))
}

export const trialDurationLabel = days => {
  const value = finiteNonNegative(days)
  if (!value) return ''
  if (value % 30 === 0) return `${value / 30} ay`
  return `${value} gün`
}

export const formatTry = (value, currency = SUBSCRIPTION_DISPLAY_TERMS.currency) => new Intl.NumberFormat('tr-TR', {
  style: 'currency',
  currency,
  minimumFractionDigits: 0,
  maximumFractionDigits: 0,
}).format(value)

export const formatSubscriptionAccessEndDate = value => {
  if (typeof value !== 'string') return null
  const raw = value.trim()
  const match = raw.match(/^(\d{4})-(\d{2})-(\d{2})T(?:[01]\d|2[0-3]):[0-5]\d:[0-5]\d(?:\.\d{1,3})?(?:Z|[+-](?:[01]\d|2[0-3]):[0-5]\d)$/)
  if (!match) return null

  const [, yearText, monthText, dayText] = match
  const year = Number(yearText)
  const month = Number(monthText)
  const day = Number(dayText)
  const calendarDate = new Date(Date.UTC(year, month - 1, day))
  if (calendarDate.getUTCFullYear() !== year
    || calendarDate.getUTCMonth() !== month - 1
    || calendarDate.getUTCDate() !== day) return null

  const date = new Date(raw)
  if (!Number.isFinite(date.getTime())) return null

  const parts = new Intl.DateTimeFormat('tr-TR', {
    timeZone: 'Europe/Istanbul',
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  }).formatToParts(date).reduce((result, part) => ({
    ...result,
    [part.type]: part.value,
  }), {})

  if (!parts.day || !parts.month || !parts.year) return null
  return `${parts.day}.${parts.month}.${parts.year}`
}

export const amountChargedToday = offer => {
  const normalized = normalizeCommercialOffer(offer)
  if (!normalized || normalized.blocked) return null
  return normalized.trialPeriodDays > 0 ? 0 : commercialTerms(offer).grossPrice
}
